/**
 * Service para gerenciamento de membros via Firestore direto
 */
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../config/firebase";

const COLLECTION = "membros";
const membrosRef = collection(db, COLLECTION);

/**
 * Converte Firestore Timestamp para string ISO
 */
const toDateString = (val) => {
  if (!val) return null;
  if (val.toDate) return val.toDate().toISOString();
  if (val.seconds) return new Date(val.seconds * 1000).toISOString();
  return val;
};

/**
 * Normaliza documento Firestore para objeto JS
 */
const docToMembro = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    data_nascimento: toDateString(data.data_nascimento),
    data_entrada: toDateString(data.data_entrada),
    data_casamento: toDateString(data.data_casamento),
    data_nascimento_conjuge: toDateString(data.data_nascimento_conjuge),
    data_batismo: toDateString(data.data_batismo),
    created_at: toDateString(data.created_at),
    updated_at: toDateString(data.updated_at),
  };
};

/**
 * Obtém o próximo ID numérico para um membro (atômico)
 */
async function getNextMemberId() {
  const counterRef = doc(db, "_counters", "membros");
  const newId = await runTransaction(db, async (transaction) => {
    const counterDoc = await transaction.get(counterRef);
    const currentValue = counterDoc.exists() ? counterDoc.data().current : 0;
    const nextValue = currentValue + 1;
    transaction.set(counterRef, { current: nextValue }, { merge: true });
    return String(nextValue);
  });
  return newId;
}

/**
 * Verifica se CPF já existe (para evitar duplicatas)
 */
async function cpfJaExiste(cpf, excludeId = null) {
  const q = query(membrosRef, where("cpf", "==", cpf));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return false;
  if (excludeId) {
    return snapshot.docs.some((d) => d.id !== excludeId);
  }
  return true;
}

/**
 * Formatação de valores para exibição
 */
export const formatarCPF = (cpf) => {
  if (!cpf) return "";
  const cleaned = cpf.replace(/\D/g, "");
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

export const formatarTelefone = (telefone) => {
  if (!telefone) return "";
  const cleaned = telefone.replace(/\D/g, "");
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  } else if (cleaned.length === 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return telefone;
};

export const formatarData = (data) => {
  if (!data) return "";
  try {
    const date = typeof data === "string" ? new Date(data) : data;
    return date.toLocaleDateString("pt-BR");
  } catch (error) {
    return data;
  }
};

export const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  try {
    const nascimento =
      typeof dataNascimento === "string"
        ? new Date(dataNascimento)
        : dataNascimento;
    const hoje = new Date();
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();

    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }

    return idade;
  } catch (error) {
    return null;
  }
};

/**
 * Labels para exibição
 */
export const TIPOS_MEMBRO_LABELS = {
  membro: "Membro",
  congregado: "Congregado",
  visitante: "Visitante",
  frequentador: "Frequentador",
};

export const SEXO_LABELS = {
  masculino: "Masculino",
  feminino: "Feminino",
};

export const ESTADO_CIVIL_LABELS = {
  solteiro: "Solteiro(a)",
  casado: "Casado(a)",
  divorciado: "Divorciado(a)",
  viuvo: "Viúvo(a)",
  uniao_estavel: "União Estável",
};

/**
 * Service principal para gerenciamento de membros (Firestore direto)
 */
export const membrosService = {
  /**
   * Lista membros com filtros opcionais
   * Retorna { membros: [...], total: N } para compatibilidade
   */
  async listarMembros(filtros = {}) {
    try {
      const constraints = [orderBy("nome")];

      // Filtro por status ativo
      if (filtros.ativo !== undefined && filtros.ativo !== "") {
        const ativo = filtros.ativo === true || filtros.ativo === "true";
        constraints.unshift(where("ativo", "==", ativo));
      }

      // Filtro por tipo
      if (filtros.tipo && filtros.tipo !== "") {
        constraints.unshift(where("tipo", "==", filtros.tipo));
      }

      // Filtro por sexo
      if (filtros.sexo && filtros.sexo !== "") {
        constraints.unshift(where("sexo", "==", filtros.sexo));
      }

      // Filtro por possui_filhos
      if (filtros.possui_filhos !== undefined && filtros.possui_filhos !== "") {
        const possuiFilhos =
          filtros.possui_filhos === true || filtros.possui_filhos === "true";
        constraints.unshift(where("possui_filhos", "==", possuiFilhos));
      }

      const q = query(membrosRef, ...constraints);
      const snapshot = await getDocs(q);

      let membros = snapshot.docs.map(docToMembro);

      // Filtro por nome (client-side, Firestore não tem LIKE)
      if (filtros.nome && filtros.nome !== "") {
        const nomeBusca = filtros.nome.toLowerCase();
        membros = membros.filter((m) =>
          (m.nome || "").toLowerCase().includes(nomeBusca)
        );
      }

      // Filtro por email (client-side)
      if (filtros.email && filtros.email !== "") {
        const emailBusca = filtros.email.toLowerCase().trim();
        membros = membros.filter(
          (m) => (m.email || "").toLowerCase().trim() === emailBusca
        );
      }

      return { membros, total: membros.length };
    } catch (error) {
      console.error("Erro ao listar membros:", error);
      throw error;
    }
  },

  /**
   * Busca membros por nome
   */
  async buscarMembrosPorNome(nome, limitCount = 20) {
    try {
      const q = query(membrosRef, where("ativo", "==", true), orderBy("nome"));
      const snapshot = await getDocs(q);
      const nomeBusca = nome.toLowerCase();

      const membros = snapshot.docs
        .map(docToMembro)
        .filter((m) => (m.nome || "").toLowerCase().includes(nomeBusca))
        .slice(0, limitCount);

      return { membros, total: membros.length };
    } catch (error) {
      console.error("Erro ao buscar membros por nome:", error);
      throw error;
    }
  },

  /**
   * Busca um membro por CPF
   */
  async buscarPorCpf(cpf) {
    try {
      const cpfLimpo = cpf.replace(/\D/g, "");
      const q = query(
        membrosRef,
        where("cpf", "==", cpfLimpo),
        firestoreLimit(1)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) return null;
      return docToMembro(snapshot.docs[0]);
    } catch (error) {
      console.error("Erro ao buscar membro por CPF:", error);
      return null;
    }
  },

  /**
   * Obtém um membro específico por ID
   * Retorna { membro: {...} } para compatibilidade
   */
  async obterMembro(id) {
    try {
      const docSnap = await getDoc(doc(membrosRef, String(id)));
      if (!docSnap.exists()) {
        throw new Error("Membro não encontrado");
      }
      return { membro: docToMembro(docSnap) };
    } catch (error) {
      console.error("Erro ao obter membro:", error);
      throw error;
    }
  },

  /**
   * Cria um novo membro com ID auto-incrementado
   */
  async criarMembro(dadosMembro, userId) {
    try {
      const cpfLimpo = (dadosMembro.cpf || "").replace(/\D/g, "");

      // Verificar CPF duplicado
      if (cpfLimpo && (await cpfJaExiste(cpfLimpo))) {
        throw new Error("CPF já cadastrado no sistema");
      }

      const nextId = await getNextMemberId();

      const dados = {
        ...dadosMembro,
        cpf: cpfLimpo,
        telefone: (dadosMembro.telefone || "").replace(/\D/g, ""),
        ativo: dadosMembro.ativo !== undefined ? dadosMembro.ativo : true,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userId || null,
      };

      await setDoc(doc(membrosRef, nextId), dados);

      return { sucesso: true, id: nextId, mensagem: "Membro criado com sucesso" };
    } catch (error) {
      console.error("Erro ao criar membro:", error);
      throw error;
    }
  },

  /**
   * Cria um novo membro com ID específico (para migração)
   */
  async criarMembroComId(dadosMembro, userId) {
    try {
      const id = String(dadosMembro.id);
      const dados = {
        ...dadosMembro,
        cpf: (dadosMembro.cpf || "").replace(/\D/g, ""),
        telefone: (dadosMembro.telefone || "").replace(/\D/g, ""),
        ativo: dadosMembro.ativo !== undefined ? dadosMembro.ativo : true,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userId || null,
      };
      delete dados.id;

      await setDoc(doc(membrosRef, id), dados);

      return { sucesso: true, id, mensagem: "Membro criado com sucesso" };
    } catch (error) {
      console.error("Erro ao criar membro com ID:", error);
      throw error;
    }
  },

  /**
   * Atualiza um membro existente
   */
  async atualizarMembro(dadosMembro, userId) {
    try {
      const id = String(dadosMembro.id);
      const cpfLimpo = (dadosMembro.cpf || "").replace(/\D/g, "");

      // Verificar CPF duplicado (excluindo o próprio membro)
      if (cpfLimpo && (await cpfJaExiste(cpfLimpo, id))) {
        throw new Error("CPF já cadastrado para outro membro");
      }

      const dados = { ...dadosMembro };
      delete dados.id;

      if (dados.cpf) dados.cpf = cpfLimpo;
      if (dados.telefone) dados.telefone = dados.telefone.replace(/\D/g, "");
      dados.updated_at = serverTimestamp();
      dados.updated_by = userId || null;

      await updateDoc(doc(membrosRef, id), dados);

      return { sucesso: true, mensagem: "Membro atualizado com sucesso" };
    } catch (error) {
      console.error("Erro ao atualizar membro:", error);
      throw error;
    }
  },

  /**
   * Desativa um membro (soft delete)
   */
  async deletarMembro(id) {
    try {
      await updateDoc(doc(membrosRef, String(id)), {
        ativo: false,
        updated_at: serverTimestamp(),
      });
      return { sucesso: true, mensagem: "Membro desativado com sucesso" };
    } catch (error) {
      console.error("Erro ao desativar membro:", error);
      throw error;
    }
  },

  /**
   * Exclui um membro permanentemente do Firestore
   */
  async excluirMembroPermanente(id) {
    try {
      await deleteDoc(doc(membrosRef, String(id)));
      return { sucesso: true, mensagem: "Membro excluído permanentemente" };
    } catch (error) {
      console.error("Erro ao excluir membro:", error);
      throw error;
    }
  },

  /**
   * Gera relatório estatístico dos membros
   */
  async relatorioMembros() {
    try {
      const snapshot = await getDocs(membrosRef);
      const membros = snapshot.docs.map(docToMembro);

      const ativos = membros.filter((m) => m.ativo !== false);
      const inativos = membros.filter((m) => m.ativo === false);

      // Contar por tipo
      const porTipo = {};
      ativos.forEach((m) => {
        const tipo = m.tipo || "membro";
        porTipo[tipo] = (porTipo[tipo] || 0) + 1;
      });

      // Contar por sexo
      const porSexo = {};
      ativos.forEach((m) => {
        const sexo = m.sexo || "nao_informado";
        porSexo[sexo] = (porSexo[sexo] || 0) + 1;
      });

      return {
        relatorio: {
          total_membros: membros.length,
          membros_ativos: ativos.length,
          membros_inativos: inativos.length,
          por_tipo: porTipo,
          por_sexo: porSexo,
        },
      };
    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      throw error;
    }
  },

  /**
   * Valida CPF
   */
  validarCPF(cpf) {
    if (!cpf) return false;

    const cleaned = cpf.replace(/\D/g, "");

    if (cleaned.length !== 11) return false;

    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cleaned)) return false;

    // Validação do primeiro dígito verificador
    let soma = 0;
    for (let i = 0; i < 9; i++) {
      soma += parseInt(cleaned.charAt(i)) * (10 - i);
    }
    let resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cleaned.charAt(9))) return false;

    // Validação do segundo dígito verificador
    soma = 0;
    for (let i = 0; i < 10; i++) {
      soma += parseInt(cleaned.charAt(i)) * (11 - i);
    }
    resto = 11 - (soma % 11);
    if (resto === 10 || resto === 11) resto = 0;
    if (resto !== parseInt(cleaned.charAt(10))) return false;

    return true;
  },

  /**
   * Normaliza dados do membro para envio
   */
  normalizarDados(dados) {
    return {
      ...dados,
      nome: dados.nome?.trim(),
      cpf: dados.cpf?.replace(/\D/g, ""),
      telefone: dados.telefone?.replace(/\D/g, ""),
      email: dados.email?.trim() || null,
      endereco: dados.endereco?.trim(),
      nome_pai: dados.nome_pai?.trim() || null,
      nome_mae: dados.nome_mae?.trim() || null,
      nome_conjuge: dados.nome_conjuge?.trim() || null,
    };
  },
};

// Classe para compatibilidade com código existente
export class MembrosService {
  static async listarMembros(filtros = {}) {
    const response = await membrosService.listarMembros(filtros);
    return response.membros || [];
  }

  static async buscarMembroPorId(id) {
    try {
      const response = await membrosService.obterMembro(id);
      return response.membro || null;
    } catch (error) {
      if (error.message.includes("não encontrado")) {
        return null;
      }
      throw error;
    }
  }

  static async buscarPorId(id) {
    return this.buscarMembroPorId(id);
  }

  static async criarMembro(membro, userId) {
    const dadosNormalizados = membrosService.normalizarDados(membro);
    const response = await membrosService.criarMembro(dadosNormalizados, userId);
    return { id: response.id, ...dadosNormalizados };
  }

  static async atualizarMembro(id, dados, userId) {
    const dadosNormalizados = membrosService.normalizarDados({ ...dados, id });
    await membrosService.atualizarMembro(dadosNormalizados, userId);
  }

  static async desativarMembro(id, userId) {
    await this.atualizarMembro(id, { ativo: false }, userId);
  }

  static async ativarMembro(id, userId) {
    await this.atualizarMembro(id, { ativo: true }, userId);
  }

  static async alternarStatusMembro(id, novoStatus) {
    await this.atualizarMembro(id, { ativo: novoStatus }, "sistema");
  }

  static async excluirMembro(id) {
    await membrosService.deletarMembro(id);
  }

  static async obterEstatisticas() {
    try {
      const response = await membrosService.relatorioMembros();
      const relatorio = response.relatorio;

      return {
        total: relatorio.total_membros,
        ativos: relatorio.membros_ativos,
        inativos: relatorio.membros_inativos,
        porTipo: relatorio.por_tipo,
        porSexo: relatorio.por_sexo,
      };
    } catch (error) {
      console.error("Erro ao obter estatísticas:", error);
      throw error;
    }
  }

  static async ajustarContador() {
    console.log("Contador gerenciado automaticamente via Firestore");
    return 0;
  }

  // Verificar acesso por email (para login)
  // Usa query direta por email para funcionar com Firestore Security Rules
  // (regra: resource.data.email == request.auth.token.email)
  static async verificarAcessoPorEmail(email) {
    try {
      const emailLimpo = (email || "").toLowerCase().trim();
      const q = query(
        membrosRef,
        where("email", "==", emailLimpo),
        firestoreLimit(1)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return {
          temAcesso: false,
          motivo: "Email não encontrado na base de membros",
          membro: null,
        };
      }

      const membro = docToMembro(snapshot.docs[0]);

      if (!membro.ativo) {
        return {
          temAcesso: false,
          motivo: "Membro inativo",
          membro: membro,
        };
      }

      return {
        temAcesso: true,
        motivo: "Acesso autorizado",
        membro: membro,
      };
    } catch (error) {
      console.error("Erro ao verificar acesso por email:", error);
      return {
        temAcesso: false,
        motivo: "Erro interno do sistema",
        membro: null,
      };
    }
  }

  // Verificar acesso por email (usa Firestore direto agora)
  static async verificarAcessoPorEmailPublico(email) {
    try {
      const emailLimpo = email.toLowerCase().trim();
      const q = query(
        membrosRef,
        where("email", "==", emailLimpo),
        firestoreLimit(1)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return {
          temAcesso: false,
          motivo: "Credenciais inválidas",
          membro: null,
        };
      }

      const membroData = snapshot.docs[0].data();
      const membroId = snapshot.docs[0].id;

      if (membroData.ativo === false) {
        return {
          temAcesso: false,
          motivo: "Conta desativada",
          membro: null,
        };
      }

      return {
        temAcesso: true,
        motivo: "Acesso autorizado",
        membro: {
          id: membroId,
          primeiroNome: (membroData.nome || "Usuário").split(" ")[0],
          tipo: membroData.tipo || "membro",
          nome: membroData.nome,
          telefone: membroData.telefone,
          endereco: membroData.endereco,
        },
      };
    } catch (error) {
      console.error("Erro ao verificar acesso por email (público):", error);
      return {
        temAcesso: false,
        motivo: "Erro interno do sistema",
        membro: null,
      };
    }
  }
}
